/**
 * Event detail page — hero, dates/times, venue map, organizer, description,
 * "I'm interested" toggle, share, tags, and Wikipedia-style attribution.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ShareButton from "@/components/ShareButton";
import {
  MapPin, Calendar, Clock, Users, ExternalLink, Share2, Heart, DollarSign,
  Baby, Dog, Accessibility, Bus, Car, Sparkles, AlertTriangle,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function EventDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [interested, setInterested] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setEv(null);
    setNotFound(false);
    http.get(`/events/${slug}`)
      .then((r) => {
        setEv(r.data);
        if (user && (r.data.interested_user_ids || []).includes(user.id)) setInterested(true);
      })
      .catch(() => setNotFound(true));
  }, [slug, user]);

  const toggleInterested = async () => {
    if (!user) return toast.error("Please sign in to save events");
    try {
      const { data } = await http.post(`/events/${ev.id}/interested`);
      setInterested(!!data.interested);
      setEv((e) => e ? { ...e, interested_count: (e.interested_count || 0) + (data.interested ? 1 : -1) } : e);
      toast.success(data.interested ? "Added to interested list" : "Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const share = async () => {
    const url = `https://socialpulse.com/events/${ev.slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: ev.title, url }); return; } catch {}
    }
    await navigator.clipboard.writeText(url);
    toast.success("Event link copied");
  };

  if (notFound) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="overline">EVENTS</div>
        <h1 className="font-display text-3xl mt-3">We couldn't find that event.</h1>
        <Link to="/events" className="wofy-btn-primary mt-6 inline-flex">Back to Events</Link>
      </div>
    );
  }
  if (!ev) return <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">Loading…</div>;

  const mapUrl = ev.latitude && ev.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${ev.latitude},${ev.longitude}`
    : ev.venue ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([ev.venue, ev.city, ev.country].filter(Boolean).join(", "))}` : null;
  const embedUrl = ev.latitude && ev.longitude
    ? `https://www.google.com/maps?q=${ev.latitude},${ev.longitude}&hl=en&z=15&output=embed`
    : null;

  return (
    <div data-testid="event-detail" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
      <Helmet>
        <title>{`${ev.title} — Social Pulse Events`}</title>
        <meta name="description" content={ev.short_description || ev.title} />
        <link rel="canonical" href={`https://socialpulse.com/events/${ev.slug}`} />
        <meta property="og:type" content="event" />
        <meta property="og:title" content={ev.title} />
        <meta property="og:description" content={ev.short_description || ev.title} />
        {ev.cover_image && <meta property="og:image" content={ev.cover_image} />}
        <meta property="og:url" content={`https://socialpulse.com/events/${ev.slug}`} />
      </Helmet>

      <div className="text-[11px] font-mono uppercase tracking-wider mb-4 opacity-60">
        <Link to="/events" className="hover:opacity-100">Events</Link>
        <span className="mx-2">/</span>
        <span>{ev.category}</span>
        {ev.subcategory && <><span className="mx-2">/</span><span>{ev.subcategory}</span></>}
      </div>

      {/* Hero */}
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div
            className="w-full aspect-[16/9] rounded-3xl bg-cover bg-center border"
            style={{
              backgroundImage: `url(${ev.cover_image || (ev.images && ev.images[0]) || ""})`,
              backgroundColor: "var(--wofy-surface)",
              borderColor: "var(--wofy-border)",
            }}
          />
          <h1 className="font-display text-4xl md:text-5xl mt-6 leading-tight">{ev.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              {fmtDate(ev.start_date)}
            </span>
            {(ev.start_date || ev.end_date) && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
                {fmtTime(ev.start_date)}{ev.end_date ? ` — ${fmtTime(ev.end_date)}` : ""}
                {ev.time_zone && <span className="opacity-60"> ({ev.time_zone})</span>}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              {[ev.venue, ev.city, ev.country].filter(Boolean).join(" · ")}
            </span>
          </div>

          {ev.recurring && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full" style={{ background: "var(--wofy-surface)" }}>
              <Sparkles className="w-3 h-3" style={{ color: "var(--wofy-primary)" }} />
              Recurring — {ev.recurrence_pattern || "check with organizer"}
            </div>
          )}

          {ev.short_description && (
            <p className="mt-6 text-lg leading-relaxed">{ev.short_description}</p>
          )}

          {ev.ai_summary && (
            <div className="mt-6 wofy-card">
              <div className="overline flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI EDITORIAL SUMMARY</div>
              <p className="mt-2 leading-relaxed">{ev.ai_summary}</p>
            </div>
          )}

          {ev.detailed_description && (
            <section className="mt-8">
              <h2 className="font-display text-2xl mb-3">About this event</h2>
              <div className="prose prose-sm max-w-none whitespace-pre-line leading-relaxed" style={{ color: "var(--wofy-text)" }}>
                {ev.detailed_description}
              </div>
            </section>
          )}

          {ev.volunteer_opportunities?.length > 0 && (
            <section className="mt-8">
              <h2 className="font-display text-2xl mb-3">Volunteer opportunities</h2>
              <ul className="space-y-1.5 text-sm">
                {ev.volunteer_opportunities.map((v, i) => (
                  <li key={i} className="flex items-start gap-2"><span style={{ color: "var(--wofy-primary)" }}>›</span><span>{v}</span></li>
                ))}
              </ul>
            </section>
          )}

          {(ev.items_to_bring || ev.weather_notes || ev.safety_instructions) && (
            <section className="mt-8 grid md:grid-cols-3 gap-4">
              {ev.items_to_bring && (
                <div className="wofy-card">
                  <div className="overline mb-1">BRING</div>
                  <p className="text-sm">{ev.items_to_bring}</p>
                </div>
              )}
              {ev.weather_notes && (
                <div className="wofy-card">
                  <div className="overline mb-1">WEATHER NOTES</div>
                  <p className="text-sm">{ev.weather_notes}</p>
                </div>
              )}
              {ev.safety_instructions && (
                <div className="wofy-card" style={{ borderColor: "#F59E0B" }}>
                  <div className="overline mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> SAFETY</div>
                  <p className="text-sm">{ev.safety_instructions}</p>
                </div>
              )}
            </section>
          )}

          {embedUrl && (
            <section className="mt-8">
              <h2 className="font-display text-2xl mb-3">Getting there</h2>
              <iframe
                data-testid="event-map"
                title={`Map for ${ev.title}`}
                src={embedUrl}
                width="100%"
                height="360"
                style={{ border: 0, borderRadius: 16 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </section>
          )}

          {ev.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {ev.tags.map((t) => (
                <span key={t} className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-5">
          <div className="wofy-card">
            <button
              onClick={toggleInterested}
              data-testid="event-interested-btn"
              className={`w-full ${interested ? "wofy-btn-primary" : "wofy-btn-ghost"} justify-center`}
            >
              <Heart className={`w-4 h-4 ${interested ? "fill-current" : ""}`} />
              {interested ? "You're interested" : "I'm interested"}
              {ev.interested_count > 0 && <span className="opacity-70 text-xs ml-1">({ev.interested_count})</span>}
            </button>
            {ev.registration_url && (
              <a
                href={ev.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-registration-link"
                className="wofy-btn-primary w-full mt-3 justify-center"
                style={{ background: "var(--wofy-secondary)" }}
              >
                Register / RSVP <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button onClick={share} data-testid="event-share-btn" className="wofy-btn-ghost w-full mt-3 justify-center">
              <Share2 className="w-4 h-4" /> Share event
            </button>
            <div className="mt-2 flex justify-center">
              <ShareButton
                title={ev.title}
                label={`Event — ${ev.title}`}
                testId="event-splink-btn"
              />
            </div>
          </div>

          <div className="wofy-card space-y-2 text-sm">
            <div className="overline mb-2">EVENT INFO</div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 opacity-60" />
              <span>{ev.admission || "Free"}{ev.ticket_price ? ` — ${ev.ticket_price}` : ""}</span>
            </div>
            {ev.family_friendly && <div className="flex items-center gap-2"><Baby className="w-4 h-4 opacity-60" /> Family-friendly</div>}
            {ev.pets_allowed && <div className="flex items-center gap-2"><Dog className="w-4 h-4 opacity-60" /> Pets allowed</div>}
            {ev.accessibility && <div className="flex items-center gap-2"><Accessibility className="w-4 h-4 opacity-60" /> {ev.accessibility}</div>}
            {ev.parking && <div className="flex items-center gap-2"><Car className="w-4 h-4 opacity-60" /> {ev.parking}</div>}
            {ev.public_transport && <div className="flex items-center gap-2"><Bus className="w-4 h-4 opacity-60" /> {ev.public_transport}</div>}
            {ev.age_restrictions && <div className="text-xs opacity-70">Age: {ev.age_restrictions}</div>}
            {ev.max_participants && <div className="flex items-center gap-2"><Users className="w-4 h-4 opacity-60" /> Max {ev.max_participants}</div>}
          </div>

          {(ev.organizer_name || ev.organization) && (
            <div className="wofy-card">
              <div className="overline mb-2">ORGANIZER</div>
              <div className="font-display text-lg">{ev.organizer_name || ev.organization}</div>
              {ev.organization && ev.organizer_name && <div className="text-xs opacity-70">{ev.organization}</div>}
              <div className="mt-3 space-y-1 text-xs">
                {ev.website && <div><a href={ev.website} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--wofy-primary)" }}>Website ↗</a></div>}
                {ev.email && <div>{ev.email}</div>}
                {ev.phone && <div>{ev.phone}</div>}
              </div>
            </div>
          )}

          {/* Aggregated event links (website + video + Meetup + Eventbrite + others) */}
          {(ev.website || ev.youtube_url || ev.meetup_url || ev.eventbrite_url || (ev.other_links || []).length > 0) && (
            <div className="wofy-card">
              <div className="overline mb-2">EVENT LINKS</div>
              <ul className="space-y-1.5 text-xs">
                {ev.website && !(ev.organizer_name || ev.organization) && (
                  <li><a href={ev.website} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--wofy-primary)" }}>Website ↗</a></li>
                )}
                {ev.youtube_url && (
                  <li><a href={ev.youtube_url} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--wofy-primary)" }} data-testid="event-yt-link">YouTube / video ↗</a></li>
                )}
                {ev.meetup_url && (
                  <li><a href={ev.meetup_url} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--wofy-primary)" }} data-testid="event-meetup-link">Meetup ↗</a></li>
                )}
                {ev.eventbrite_url && (
                  <li><a href={ev.eventbrite_url} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--wofy-primary)" }} data-testid="event-eventbrite-link">Eventbrite ↗</a></li>
                )}
                {(ev.other_links || []).map((link, i) => (
                  <li key={i}><a href={link} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--wofy-primary)" }}>{link} ↗</a></li>
                ))}
              </ul>
            </div>
          )}

          {mapUrl && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="wofy-card block hover:border-[color:var(--wofy-primary)]">
              <div className="overline mb-1">OPEN IN MAPS</div>
              <div className="font-display text-base">{ev.venue || ev.city}</div>
              {ev.street_address && <div className="text-xs opacity-70 mt-1">{ev.street_address}</div>}
              <div className="mt-2 text-xs opacity-70 inline-flex items-center gap-1">
                Google Maps <ExternalLink className="w-3 h-3" />
              </div>
            </a>
          )}
        </aside>
      </div>
    </div>
  );
}
